import type { Message } from './types';

/**
 * localStorage persistence for a per-service widget session.
 * Keyed by service ID so multiple tenants on the same page don't collide.
 *
 * Silent fallback to an in-memory store when localStorage is unavailable
 * (private mode, cookies disabled, SSR, etc.).
 */
export interface StoredSession {
  conversation_id: string | null;
  messages: Message[];
  updated_at: number;
}

const STORAGE_PREFIX = 'hd.session.v1.';
const MAX_MESSAGES = 100;
const MAX_TEXT_PER_MESSAGE = 4000;

const memoryFallback = new Map<string, string>();

function storageAvailable(): boolean {
  try {
    const probe = '__hd_probe__';
    globalThis.localStorage.setItem(probe, probe);
    globalThis.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function readRaw(key: string): string | null {
  if (storageAvailable()) {
    return globalThis.localStorage.getItem(key);
  }
  return memoryFallback.get(key) ?? null;
}

function writeRaw(key: string, value: string): void {
  if (storageAvailable()) {
    try {
      globalThis.localStorage.setItem(key, value);
      return;
    } catch {
      /* quota exceeded — fall through to memory */
    }
  }
  memoryFallback.set(key, value);
}

function removeRaw(key: string): void {
  if (storageAvailable()) {
    try {
      globalThis.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  memoryFallback.delete(key);
}

export function loadSession(serviceId: string): StoredSession | null {
  const raw = readRaw(STORAGE_PREFIX + serviceId);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!Array.isArray(parsed.messages)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(serviceId: string, session: StoredSession): void {
  // Cap history to avoid unbounded growth + truncate over-long bodies.
  const trimmed: StoredSession = {
    conversation_id: session.conversation_id,
    updated_at: Date.now(),
    messages: session.messages.slice(-MAX_MESSAGES).map((m) => ({
      ...m,
      text: m.text.length > MAX_TEXT_PER_MESSAGE ? m.text.slice(0, MAX_TEXT_PER_MESSAGE) : m.text,
    })),
  };
  try {
    writeRaw(STORAGE_PREFIX + serviceId, JSON.stringify(trimmed));
  } catch {
    /* non-fatal */
  }
}

export function clearSession(serviceId: string): void {
  removeRaw(STORAGE_PREFIX + serviceId);
}
